/**
 * Ids for the people in a household.
 *
 * A member id is permanent for the same reason a task id is, and one step
 * further: it is the *third* component of every completion id ever recorded for
 * that person, and that id is half of the primary key of the row, locally and
 * remotely. Renaming one orphans every checkmark they ever ticked — they stay
 * in the database, invisible, and the person reappears with an empty history.
 * So an id is minted once, here, and never derived from a display name or a
 * username, both of which are exactly the fields somebody edits when they fix a
 * typo.
 *
 * The shape matches what `ensure_household` mints server-side (`m_` and hex):
 * the two namespaces have to be interchangeable, because the owner's row comes
 * from Postgres and everybody else's comes from here. Only the prefix is load
 * bearing — the id is opaque everywhere it is read.
 *
 * This file is `src/lib/task-id.ts` for people, deliberately down to the shape
 * of its two functions. If one of them ever needs fixing, so does the other.
 */

export const MEMBER_ID_PREFIX = 'm_'

/**
 * A fresh id: the prefix, the minute it was created, and some noise.
 *
 * The timestamp is there to be read by a human looking at a table, not for
 * ordering — nothing sorts on it. The random tail is what stops two devices
 * offline at the same moment producing one collision instead of two people, and
 * a collision here is worse than a duplicate row: the two members would share
 * every checkmark.
 */
export function newMemberId(): string {
  const stamp = Date.now().toString(36)
  const noise = Math.random().toString(36).slice(2, 10).padEnd(8, '0')
  return `${MEMBER_ID_PREFIX}${stamp}${noise}`
}

/**
 * Whether an id can safely be put through `encodeCompletionId`.
 *
 * `encodeCompletionId` *throws* on a colon, by design, because everything it is
 * given is supposed to be a literal or a minted id. Member rows arrive from the
 * server and out of `localStorage`, where a colon is possible and a throw inside
 * the day list's render would take the whole page down. The member picker
 * filters on this, so such a row is merely unpickable — the same trade
 * `isUsableTaskId` makes, for the same reason.
 */
export function isUsableMemberId(id: string): boolean {
  return id !== '' && !id.includes(':')
}
